import { useContext } from 'react';

import { mdiTimerOutline, mdiAccountVoice, mdiAccountGroupOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { getTimeMetric } from './getTimeMetric';
import { useDashboardData } from './useDashboardData';
import { DashboardContext } from '../../context/DashboardContext';

const TimeRowPaper = styled(Paper)`
    margin-top: 12px;
`;

const GridTimeItem = styled(Grid)`
    text-align: center;
    display: flex;
    flex-direction: column;
    padding: 12px;
    align-items: center;
`;

const RowContainer = styled(Grid)`
    padding: 12px;
`;

const CategoryLargeIcon = styled(Icon)`
    width: 28px;
    color: #3a3c41;
`;

const TimeRowTitleText = styled(Typography)`
    display: inline;
    font-family: Lato;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: 0.15000000596046448px;
    text-align: center;
    color: #3a3c41;
    margin-left: 8px;
`;

const TimeRowTitle = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const PaperCircleText = styled(Typography)`
    font-family: Lato;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;
    letter-spacing: 0px;
    text-align: center;
    color: #ffffff;
`;

const PaperCircle = styled(({ circleColor, ...otherProps }) => <Paper {...otherProps} />)`
    margin-top: 7px;
    border-radius: 50%;
    width: 100px;
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${(props) => props.circleColor};
`;

const AverageTextLabel = styled(Typography)`
    color: #56585f;
    margin-top: 3px;
    font-family: Lato;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.10000000149011612px;
    text-align: center;
`;

const AverageTextValue = styled(Typography)`
    color: #56585f;
    font-family: Lato;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.10000000149011612px;
    text-align: center;
`;

const renderTimeValueInCircle = (timeString: string) => {
    if (timeString.includes(', ')) {
        const times = timeString.split(', ');
        return (
            <>
                <div>{times[0]},</div>
                <div>{times[1]}</div>
            </>
        );
    }
    return timeString;
};

const TimeRowItem = ({ titleText, titleIcon, value, circleColor, averageMetricValue, id }) => {
    const { t } = useTranslation('dashboard');

    return (
        <GridTimeItem item xs={6} sm={6} md={4} id={id}>
            <TimeRowTitle>
                <CategoryLargeIcon path={titleIcon} />
                <TimeRowTitleText variant="h2">{titleText}</TimeRowTitleText>
            </TimeRowTitle>
            <PaperCircle elevation={4} circleColor={circleColor}>
                <PaperCircleText data-testid="value">{renderTimeValueInCircle(value)}</PaperCircleText>
            </PaperCircle>
            <AverageTextLabel>{t('semester-average-2')}</AverageTextLabel>
            <AverageTextValue data-testid="average-value">{averageMetricValue}</AverageTextValue>
        </GridTimeItem>
    );
};

const DashboardSessionTimeRow: React.FC = () => {
    const { metricTranslations } = useContext(DashboardContext);
    const { sessionSummary: summary, classAverages } = useDashboardData();
    const { t } = useTranslation('dashboard');
    return (
        <TimeRowPaper>
            <RowContainer container justifyContent="center">
                <TimeRowItem
                    id="dashboard-metric-session-time"
                    titleText={t('session-time')}
                    titleIcon={mdiTimerOutline}
                    value={getTimeMetric(summary.classTime, metricTranslations)}
                    circleColor={'#AD2BE9'}
                    averageMetricValue={getTimeMetric(classAverages?.classTime, metricTranslations)}
                />
                <TimeRowItem
                    id="dashboard-metric-teacher-talk-time"
                    titleText={t('teacher-talk-time')}
                    titleIcon={mdiAccountVoice}
                    value={getTimeMetric(summary.talkTime.teacher, metricTranslations)}
                    circleColor={'#01827F'}
                    averageMetricValue={getTimeMetric(classAverages?.talkTime.teacher, metricTranslations)}
                />
                <TimeRowItem
                    id="dashboard-metric-student-talk-time"
                    titleText={t('student-talk-time')}
                    titleIcon={mdiAccountGroupOutline}
                    value={getTimeMetric(summary.talkTime.student, metricTranslations)}
                    circleColor={'#4739E7'}
                    averageMetricValue={getTimeMetric(classAverages?.talkTime.student, metricTranslations)}
                />
            </RowContainer>
        </TimeRowPaper>
    );
};

export default DashboardSessionTimeRow;
